import { useState } from 'react';
import Card from 'components/card';
import { MdSend, MdPeople, MdSchool, MdSupervisorAccount, MdAnnouncement, MdFilterList } from 'react-icons/md';

interface Announcement {
  id: string;
  title: string;
  message: string;
  audience: 'all' | 'mentees' | 'mentors';
  createdBy: string;
  createdAt: string;
  status: 'draft' | 'sent';
  deliveredTo: number;
}

const Announcements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([
    {
      id: '1',
      title: 'Welcome to Latte Mentorship Platform',
      message: 'We\'re excited to have you join our mentorship community! This platform connects mentees with experienced mentors to help accelerate your growth.',
      audience: 'all',
      createdBy: 'Admin',
      createdAt: '2025-01-09T10:00:00.000Z',
      status: 'sent',
      deliveredTo: 247
    },
    {
      id: '2',
      title: 'New Meeting Features Available',
      message: 'We\'ve added new features to make scheduling and managing meetings easier. Check out the updated calendar integration and automated reminders.',
      audience: 'all',
      createdBy: 'Admin',
      createdAt: '2025-01-08T14:30:00.000Z',
      status: 'sent',
      deliveredTo: 235
    },
    {
      id: '3',
      title: 'Mentee Onboarding Session',
      message: 'Join us for a live onboarding session next Tuesday at 2 PM EST. We\'ll walk through platform features and answer your questions. An invitation has been sent to your inbox.',
      audience: 'mentees',
      createdBy: 'Admin',
      createdAt: '2025-01-07T09:15:00.000Z',
      status: 'sent',
      deliveredTo: 143
    },
    {
      id: '4',
      title: 'Mentor Best Practices Workshop',
      message: 'Calling all mentors! Join our workshop on effective mentoring techniques and how to make the most of your mentoring relationships. An invitation has been sent to your inbox.',
      audience: 'mentors',
      createdBy: 'Admin',
      createdAt: '2025-01-06T16:45:00.000Z',
      status: 'sent',
      deliveredTo: 89
    }
  ]);

  const [filterAudience, setFilterAudience] = useState<string>('all');
  const [newAnnouncement, setNewAnnouncement] = useState<Partial<Announcement>>({
    title: '',
    message: '',
    audience: 'all'
  });

  // Filter announcements
  const filteredAnnouncements = announcements.filter(announcement => 
    filterAudience === 'all' || announcement.audience === filterAudience
  );

  // Get audience icon
  const getAudienceIcon = (audience: Announcement['audience']) => {
    switch (audience) {
      case 'mentees':
        return <MdSchool className="h-4 w-4 text-blue-500" />;
      case 'mentors':
        return <MdSupervisorAccount className="h-4 w-4 text-green-500" />;
      case 'all':
        return <MdPeople className="h-4 w-4 text-purple-500" />;
    }
  };

  // Get audience label
  const getAudienceLabel = (audience: Announcement['audience']) => {
    switch (audience) {
      case 'mentees': return 'Mentees Only';
      case 'mentors': return 'Mentors Only';
      case 'all': return 'All Users';
    }
  };

  // Format time
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle create announcement
  const handleCreateAnnouncement = () => {
    if (!newAnnouncement.title || !newAnnouncement.message) return;

    // Simulate delivery count based on audience
    let deliveredCount = 0;
    switch (newAnnouncement.audience) {
      case 'all':
        deliveredCount = Math.floor(Math.random() * 50) + 200; // 200-250 users
        break;
      case 'mentees':
        deliveredCount = Math.floor(Math.random() * 30) + 120; // 120-150 users
        break;
      case 'mentors':
        deliveredCount = Math.floor(Math.random() * 20) + 70; // 70-90 users
        break;
    }

    const announcement: Announcement = {
      id: Date.now().toString(),
      title: newAnnouncement.title,
      message: newAnnouncement.message,
      audience: newAnnouncement.audience as 'all' | 'mentees' | 'mentors',
      createdBy: 'Admin',
      createdAt: new Date().toISOString(),
      status: 'sent',
      deliveredTo: deliveredCount
    };

    setAnnouncements(prev => [announcement, ...prev]);
    setNewAnnouncement({ title: '', message: '', audience: 'all' });
  };

  return (
    <div className="mt-3 flex h-full w-full gap-5">
      {/* Announcements List */}
      <div className="w-full lg:w-[60%]">
        <Card extra="w-full h-full p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/10">
                <MdAnnouncement className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                  Announcements
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Effortlessly broadcast to targeted user groups
                </p>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="mb-4 flex items-center gap-3">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <div className="flex gap-2">
              {['all', 'mentees', 'mentors'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterAudience(filter)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                    filterAudience === filter
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Announcements */}
          <div className="space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {filteredAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:bg-gray-50 dark:hover:bg-navy-800"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      {getAudienceIcon(announcement.audience)}
                      <span className="text-xs font-medium text-gray-500">
                        {getAudienceLabel(announcement.audience)}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs font-semibold text-brand-500 bg-brand-50 dark:bg-brand-900/20 px-2 py-0.5 rounded">
                        Delivered to {announcement.deliveredTo} users
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {formatTime(announcement.createdAt)}
                      </span>
                    </div>
                    <h5 className="mb-2 font-semibold text-navy-700 dark:text-white">
                      {announcement.title}
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {announcement.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Create Announcement Form */}
      <div className="w-full lg:w-[40%]">
        <Card extra="w-full h-full p-6">
          <div className="flex h-full flex-col">
            <div className="mb-6">
              <h5 className="text-lg font-bold text-navy-700 dark:text-white">
                Create New Announcement
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Quick message broadcast - select audience and send
              </p>
            </div>

            <div className="flex-1 space-y-4">
              {/* Title */}
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Title
                </label>
                <input
                  type="text"
                  value={newAnnouncement.title || ''}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter announcement title"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                />
              </div>

              {/* Audience */}
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Audience
                </label>
                <select
                  value={newAnnouncement.audience || 'all'}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, audience: e.target.value as 'all' | 'mentees' | 'mentors' }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                >
                  <option value="all">All Users</option>
                  <option value="mentees">Mentees Only</option>
                  <option value="mentors">Mentors Only</option>
                </select>
              </div>

              {/* Message */}
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Message
                </label>
                <textarea
                  value={newAnnouncement.message || ''}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter your announcement message..."
                  rows={8}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white resize-none"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6">
              <button
                onClick={handleCreateAnnouncement}
                disabled={!newAnnouncement.title || !newAnnouncement.message}
                className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
                  newAnnouncement.title && newAnnouncement.message
                    ? 'bg-brand-500 text-white hover:bg-brand-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <MdSend className="h-4 w-4" />
                Send Announcement
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Announcements;